import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, CheckCircle, Code, Terminal, FileText, Play, AlertCircle } from 'lucide-react';

const CodeExamples = () => {
  const [activeTab, setActiveTab] = useState('javascript');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = async (code: string, id: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const languages = [
    { id: 'javascript', name: 'JavaScript', icon: <Code className="w-4 h-4" /> },
    { id: 'python', name: 'Python', icon: <Terminal className="w-4 h-4" /> },
    { id: 'curl', name: 'cURL', icon: <FileText className="w-4 h-4" /> }
  ];

  const examples = {
    javascript: {
      setup: `// Install dependencies
// npm install axios crypto

const axios = require('axios');
const crypto = require('crypto');

const WAYJA_BASE_URL = 'https://uyanvkwkikftzpanolmw.supabase.co/functions/v1';
const API_KEY = 'YOUR_API_KEY_HERE';
const SIGNING_SECRET = 'YOUR_SIGNING_SECRET_HERE'; // Required for state-modifying endpoints

// Create axios instance with default config
const wayjaClient = axios.create({
  baseURL: WAYJA_BASE_URL,
  headers: {
    'X-API-Key': API_KEY,
    'Content-Type': 'application/json'
  }
});

// Helper function to generate HMAC signature
async function generateHMACSignature(method, path, timestamp, nonce, body, signingSecret) {
  const message = \`\${method}\\n\${path}\\n\${timestamp}\\n\${nonce}\\n\${body}\`;
  const signature = crypto
    .createHmac('sha256', signingSecret)
    .update(message)
    .digest('base64')
    .replace(/\\+/g, '-')
    .replace(/\\//g, '_')
    .replace(/=/g, '');
  return signature;
}

// Helper function to add HMAC headers to requests
function addHMACHeaders(config, signingSecret) {
  const method = config.method.toUpperCase();
  // Note: Use path without /functions/v1 prefix for HMAC signing
  // Supabase Edge Functions strip the /functions/v1 prefix from the pathname
  const fullPath = new URL(config.url).pathname;
  const pathForSigning = fullPath.replace('/functions/v1', '');
  const timestamp = Date.now();
  const nonce = crypto.randomUUID();
  const body = config.data ? JSON.stringify(config.data) : '';
  
  const signature = generateHMACSignature(method, pathForSigning, timestamp, nonce, body, signingSecret);
  
  config.headers['X-Request-Timestamp'] = timestamp.toString();
  config.headers['X-Request-Nonce'] = nonce;
  config.headers['X-Request-Signature'] = signature;
  
  return config;
}`,
      marketData: `// Get market data
async function getMarketData(marketId) {
  try {
    const response = await wayjaClient.get(\`/market-data/\${marketId}\`);
    console.log('Market data:', response.data);

    // Access market options
    const options = response.data.data.options;
    options.forEach(option => {
      console.log(\`\${option.name}: \${option.probability.toFixed(2)}% (\${option.odds}x)\`);
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching market data:', error.response?.data || error.message);
  }
}

// Usage
getMarketData('market-123');`,
      marketDataByArticle: `// Get markets by article ID
async function getMarketsByArticle(articleId) {
  try {
    const response = await wayjaClient.get(\`/market-data/article/\${articleId}\`);
    console.log('Markets for article:', response.data);

    // Access markets array
    const markets = response.data.data.markets || [response.data.data];
    markets.forEach(market => {
      console.log(\`Market: \${market.title}, Volume: R\${market.total_volume}\`);
    });

    return response.data;
  } catch (error) {
    console.error('Error fetching markets by article:', error.response?.data || error.message);
  }
}

// Usage
getMarketsByArticle('article-123');`,
      walletBalance: `// Get wallet balance (read-only, no HMAC signing required)
async function getWalletBalance(userToken, apiKey) {
  try {
    // URL encode the user_token to handle special characters safely
    const encodedToken = encodeURIComponent(userToken);
    const url = \`\${WAYJA_BASE_URL}/wallet-balance?user_token=\${encodedToken}\`;
    
    const response = await wayjaClient.get(\`/wallet-balance?user_token=\${encodedToken}\`);
    
    if (response.data.success) {
      console.log('Wallet balance:', response.data.data.balance);
      console.log('Currency:', response.data.data.currency);
      console.log('User ID:', response.data.data.user_id);
      return response.data.data;
    } else {
      throw new Error(response.data.error || 'Failed to fetch wallet balance');
    }
  } catch (error) {
    console.error('Error fetching wallet balance:', error.response?.data || error.message);
    throw error;
  }
}

// Usage
const userToken = localStorage.getItem('wayja_user_token');
if (userToken) {
  getWalletBalance(userToken, API_KEY)
    .then(balance => {
      console.log('Balance:', balance);
    })
    .catch(error => {
      console.error('Error:', error.message);
    });
} else {
  console.error('User not authenticated. Please authenticate first.');
}`,
      authenticateUser: `// Users must authenticate directly with Wayja
// For web applications, redirect users to Wayja login page

// OAuth 2.0 Redirect Flow
function redirectToWayjaLogin(redirectUri) {
  const encodedRedirect = encodeURIComponent(redirectUri);
  const loginUrl = \`https://wayja.me/auth/login?redirect_uri=\${encodedRedirect}\`;
  window.location.href = loginUrl;
}

// After login, Wayja redirects back with user_token in URL
function handleOAuthCallback() {
  const urlParams = new URLSearchParams(window.location.search);
  const userToken = urlParams.get('wayja_token');
  const userId = urlParams.get('wayja_user_id');
  
  if (userToken && userId) {
    // Store token securely (use httpOnly cookies or encrypted storage)
    localStorage.setItem('wayja_user_token', userToken);
    localStorage.setItem('wayja_user_id', userId);
    
    // Remove token from URL for security
    window.history.replaceState({}, '', window.location.pathname);
    
    return { wayjaToken: userToken, userId: userId };
  }
  
  return null;
}

// Usage: Call handleOAuthCallback() on page load to check for OAuth callback
// If no token exists, call redirectToWayjaLogin(window.location.href)`,
      placeBet: `// Place a trade (requires HMAC signing)
const crypto = require('crypto');

// Generate HMAC signature for request
async function generateHMACSignature(method, path, timestamp, nonce, body, signingSecret) {
  const message = \`\${method}\\n\${path}\\n\${timestamp}\\n\${nonce}\\n\${body}\`;
  const signature = crypto
    .createHmac('sha256', signingSecret)
    .update(message)
    .digest('base64')
    .replace(/\\+/g, '-')
    .replace(/\\//g, '_')
    .replace(/=/g, '');
  return signature;
}

// Place a trade with HMAC signing
async function placeBet(optionId, amount, wayjaToken, signingSecret) {
  const method = 'POST';
  // Note: Use /place-bet (not /functions/v1/place-bet) for HMAC signing
  const pathForSigning = '/place-bet';
  const fullPath = '/functions/v1/place-bet';
  const timestamp = Date.now();
  const nonce = crypto.randomUUID();
  const body = JSON.stringify({
    option_id: optionId,
    amount: amount,
    user_token: wayjaToken
  });
  
  // Generate HMAC signature (use pathForSigning)
  const signature = await generateHMACSignature(method, pathForSigning, timestamp, nonce, body, signingSecret);
  
  try {
    const response = await axios.post(
      \`\${WAYJA_BASE_URL}\${fullPath}\`,
      JSON.parse(body),
      {
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
          'X-Request-Timestamp': timestamp.toString(),
          'X-Request-Nonce': nonce,
          'X-Request-Signature': signature
        }
      }
    );

    const { bet_id, market_id, odds, potential_win } = response.data.data;
    console.log(\`Trade placed! ID: \${bet_id}, Potential win: R\${potential_win}\`);

    return response.data;
  } catch (error) {
    console.error('Trade placement failed:', error.response?.data || error.message);
  }
}

// Create a new market
async function createMarket(marketData, signingSecret) {
  const method = 'POST';
  // Note: Use /external-create-market (not /functions/v1/external-create-market) for HMAC signing
  const pathForSigning = '/external-create-market';
  const fullPath = '/functions/v1/external-create-market';
  const timestamp = Date.now();
  const nonce = crypto.randomUUID();
  const body = JSON.stringify(marketData);
  
  // Generate HMAC signature (use pathForSigning)
  const signature = await generateHMACSignature(method, pathForSigning, timestamp, nonce, body, signingSecret);
  
  try {
    const response = await axios.post(
      \`\${WAYJA_BASE_URL}\${fullPath}\`,
      marketData,
      {
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
          'X-Request-Timestamp': timestamp.toString(),
          'X-Request-Nonce': nonce,
          'X-Request-Signature': signature
        }
      }
    );

    console.log('Market created:', response.data);
    return response.data;
  } catch (error) {
    console.error('Market creation failed:', error.response?.data || error.message);
  }
}

// Top up wallet
async function topUpWallet(amount, cardDetails, userToken, signingSecret) {
  const method = 'POST';
  // Note: Use /wallet-topup (not /functions/v1/wallet-topup) for HMAC signing
  const pathForSigning = '/wallet-topup';
  const fullPath = '/functions/v1/wallet-topup';
  const timestamp = Date.now();
  const nonce = crypto.randomUUID();
  const body = JSON.stringify({
    user_token: userToken,
    amount: amount,
    ...cardDetails
  });
  
  // Generate HMAC signature (use pathForSigning)
  const signature = await generateHMACSignature(method, pathForSigning, timestamp, nonce, body, signingSecret);
  
  try {
    const response = await axios.post(
      \`\${WAYJA_BASE_URL}\${fullPath}\`,
      JSON.parse(body),
      {
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
          'X-Request-Timestamp': timestamp.toString(),
          'X-Request-Nonce': nonce,
          'X-Request-Signature': signature
        }
      }
    );

    console.log('Top-up initiated:', response.data);
    if (response.data.data?.requires_redirect) {
      console.log('Redirect to payment:', response.data.data.redirect);
    }
    return response.data;
  } catch (error) {
    console.error('Top-up failed:', error.response?.data || error.message);
  }
}

// Usage example
async function exampleBettingFlow(signingSecret) {
  // 1. Get market data (no HMAC signing required - read-only endpoint)
  const marketData = await getMarketData('market-123');

  // 2. Get user token (from OAuth callback or stored session)
  const wayjaToken = localStorage.getItem('wayja_user_token');
  if (!wayjaToken) {
    console.error('User not authenticated. Redirect to Wayja login.');
    redirectToWayjaLogin(window.location.href);
    return;
  }

  // 3. Place trade on first option (HMAC signing required)
  if (marketData && marketData.data.options.length > 0) {
    const firstOption = marketData.data.options[0];
    await placeBet(firstOption.id, 100, wayjaToken, signingSecret);
  }
}`,
      createMarket: `// Create a new market
async function createMarket(marketData, signingSecret) {
  const method = 'POST';
  // Note: Use /external-create-market (not /functions/v1/external-create-market) for HMAC signing
  const pathForSigning = '/external-create-market';
  const fullPath = '/functions/v1/external-create-market';
  const timestamp = Date.now();
  const nonce = crypto.randomUUID();
  const body = JSON.stringify(marketData);
  
  // Generate HMAC signature (use pathForSigning)
  const signature = await generateHMACSignature(method, pathForSigning, timestamp, nonce, body, signingSecret);
  
  try {
    const response = await axios.post(
      \`\${WAYJA_BASE_URL}\${fullPath}\`,
      marketData,
      {
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
          'X-Request-Timestamp': timestamp.toString(),
          'X-Request-Nonce': nonce,
          'X-Request-Signature': signature
        }
      }
    );

    console.log('Market created:', response.data);
    return response.data;
  } catch (error) {
    console.error('Market creation failed:', error.response?.data || error.message);
  }
}

// Usage
const marketData = {
  article_id: 'article-123',
  title: 'Will Bitcoin reach $100k by end of 2024?',
  description: 'Market description here...',
  category: 'Crypto',
  market_type: 'fixed',
  options: [
    { name: 'Yes' },
    { name: 'No' }
  ],
  resolution_time: '2024-12-31T23:59:59Z',
  trading_end_time: '2024-12-31T20:00:00Z', // Required for fixed markets
  is_private: false
};

createMarket(marketData, SIGNING_SECRET);`,
      topUpWallet: `// Top up wallet
async function topUpWallet(amount, cardDetails, userToken, signingSecret) {
  const method = 'POST';
  // Note: Use /wallet-topup (not /functions/v1/wallet-topup) for HMAC signing
  const pathForSigning = '/wallet-topup';
  const fullPath = '/functions/v1/wallet-topup';
  const timestamp = Date.now();
  const nonce = crypto.randomUUID();
  const body = JSON.stringify({
    user_token: userToken,
    amount: amount,
    ...cardDetails
  });
  
  // Generate HMAC signature (use pathForSigning)
  const signature = await generateHMACSignature(method, pathForSigning, timestamp, nonce, body, signingSecret);
  
  try {
    const response = await axios.post(
      \`\${WAYJA_BASE_URL}\${fullPath}\`,
      JSON.parse(body),
      {
        headers: {
          'X-API-Key': API_KEY,
          'Content-Type': 'application/json',
          'X-Request-Timestamp': timestamp.toString(),
          'X-Request-Nonce': nonce,
          'X-Request-Signature': signature
        }
      }
    );

    console.log('Top-up initiated:', response.data);
    if (response.data.data?.requires_redirect) {
      console.log('Redirect to payment:', response.data.data.redirect);
    }
    return response.data;
  } catch (error) {
    console.error('Top-up failed:', error.response?.data || error.message);
  }
}

// Usage
const cardDetails = {
  card_number: '4111111111111111',
  card_holder: 'John Doe',
  card_expiry_month: '12',
  card_expiry_year: '2025',
  card_cvv: '123'
};

const userToken = localStorage.getItem('wayja_user_token');
topUpWallet(100, cardDetails, userToken, SIGNING_SECRET);`,
      completeExample: `// Complete example: Market monitoring and automated betting
const crypto = require('crypto');

class WayjaTrader {
  constructor(apiKey, signingSecret) {
    this.apiKey = apiKey;
    this.signingSecret = signingSecret;
    this.client = axios.create({
      baseURL: WAYJA_BASE_URL,
      headers: {
        'X-API-Key': apiKey,
        'Content-Type': 'application/json'
      }
    });
    this.userToken = null;
  }

  // Generate HMAC signature for requests
  async generateSignature(method, path, timestamp, nonce, body) {
    const message = \`\${method}\\n\${path}\\n\${timestamp}\\n\${nonce}\\n\${body}\`;
    return crypto
      .createHmac('sha256', this.signingSecret)
      .update(message)
      .digest('base64')
      .replace(/\\+/g, '-')
      .replace(/\\//g, '_')
      .replace(/=/g, '');
  }

  // Set user token (obtained from Wayja OAuth flow)
  setUserToken(token) {
    this.userToken = token;
  }

  async getMarketOdds(marketId) {
    const response = await this.client.get(\`/market-data/\${marketId}\`);
    return response.data.data.options.map(option => ({
      id: option.id,
      name: option.name,
      probability: option.probability,
      odds: option.odds,
      volume: option.volume
    }));
  }

  async placeBet(optionId, amount) {
    if (!this.userToken) throw new Error('User not authenticated');

    const method = 'POST';
    // Note: Use /place-bet (not /functions/v1/place-bet) for HMAC signing
    const pathForSigning = '/place-bet';
    const fullPath = '/functions/v1/place-bet';
    const timestamp = Date.now();
    const nonce = crypto.randomUUID();
    const body = JSON.stringify({
      option_id: optionId,
      amount: amount,
      user_token: this.userToken
    });
    
    // Generate HMAC signature (use pathForSigning)
    const signature = await this.generateSignature(method, pathForSigning, timestamp, nonce, body);
    
    const response = await this.client.post(fullPath, JSON.parse(body), {
      headers: {
        'X-Request-Timestamp': timestamp.toString(),
        'X-Request-Nonce': nonce,
        'X-Request-Signature': signature
      }
    });
    return response.data;
  }

  // Simple trading strategy: trade on underdog when odds > 2.0
  async executeStrategy(marketId, maxBet = 50) {
    const odds = await this.getMarketOdds(marketId);
    const underdog = odds.find(option => option.odds > 2.0);

    if (underdog) {
      console.log(\`Found underdog: \${underdog.name} at \${underdog.odds}x odds\`);
      return await this.placeBet(underdog.id, maxBet);
    }
  }

}

// Usage
const trader = new WayjaTrader('YOUR_API_KEY', 'YOUR_SIGNING_SECRET');

// Set user token (obtained from Wayja OAuth flow)
// In a real application, this would come from the OAuth callback
trader.setUserToken('wayja_token_from_oauth_callback');

await trader.executeStrategy('market-123');`
    },
    python: {
      setup: `# Install dependencies
# pip install requests hmac hashlib base64 uuid

import requests
import json
import hmac
import hashlib
import base64
import uuid
import time
from typing import Dict, Any, Optional

WAYJA_BASE_URL = 'https://uyanvkwkikftzpanolmw.supabase.co/functions/v1'
API_KEY = 'YOUR_API_KEY_HERE'
SIGNING_SECRET = 'YOUR_SIGNING_SECRET_HERE'  # Required for state-modifying endpoints

class WayjaClient:
    def __init__(self, api_key: str, signing_secret: str = None):
        self.api_key = api_key
        self.signing_secret = signing_secret
        self.base_url = WAYJA_BASE_URL
        self.session = requests.Session()
        self.session.headers.update({
            'X-API-Key': api_key,
            'Content-Type': 'application/json'
        })
    
    def generate_hmac_signature(self, method: str, path: str, timestamp: int, nonce: str, body: str) -> str:
        """Generate HMAC-SHA256 signature for request."""
        message = f"{method}\\n{path}\\n{timestamp}\\n{nonce}\\n{body}"
        signature = hmac.new(
            self.signing_secret.encode('utf-8'),
            message.encode('utf-8'),
            hashlib.sha256
        ).digest()
        return base64.b64encode(signature).decode('utf-8').replace('+', '-').replace('/', '_').replace('=', '')

    def _post(self, endpoint: str, data: Dict[str, Any]) -> Dict[str, Any]:
        response = self.session.post(f"{self.base_url}{endpoint}", json=data)
        response.raise_for_status()
        return response.json()

    def _get(self, endpoint: str) -> Dict[str, Any]:
        response = self.session.get(f"{self.base_url}{endpoint}")
        response.raise_for_status()
        return response.json()`,
      marketData: `# Get market data
def get_market_data(self, market_id: str) -> Dict[str, Any]:
    """Fetch market data and return formatted options."""
    try:
        response = self._get(f"/market-data/{market_id}")
        market_data = response['data']

        print(f"Market: {market_data['title']}")
        print(f"Status: {market_data['status']}")
        print(f"Total Volume: R{market_data['total_volume']}")

        for option in market_data['options']:
            print(f"  {option['name']}: {option['probability']:.1%} ({option['odds']:.2f}x)")

        return market_data
    except requests.exceptions.RequestException as e:
        print(f"Error fetching market data: {e}")
        return {}

# Usage
client = WayjaClient(API_KEY, SIGNING_SECRET)
market_data = client.get_market_data('market-123')`,
      marketDataByArticle: `# Get markets by article ID
def get_markets_by_article(self, article_id: str) -> Dict[str, Any]:
    """Fetch all markets associated with an article."""
    try:
        response = self._get(f"/market-data/article/{article_id}")
        markets_data = response['data']
        
        # Handle both array and single market response
        markets = markets_data.get('markets', [markets_data]) if isinstance(markets_data, dict) else markets_data
        
        print(f"Found {len(markets)} market(s) for article {article_id}")
        for market in markets:
            print(f"  Market: {market.get('title', 'N/A')}, Volume: R{market.get('total_volume', 0)}")
        
        return markets_data
    except requests.exceptions.RequestException as e:
        print(f"Error fetching markets by article: {e}")
        return {}

# Usage
client = WayjaClient(API_KEY, SIGNING_SECRET)
markets = client.get_markets_by_article('article-123')`,
      walletBalance: `# Get wallet balance (read-only, no HMAC signing required)
def get_wallet_balance(self, user_token: str) -> Dict[str, Any]:
    """Fetch user wallet balance."""
    try:
        from urllib.parse import quote
        encoded_token = quote(user_token)
        response = self._get(f"/wallet-balance?user_token={encoded_token}")
        
        if response.get('success'):
            balance_data = response['data']
            print(f"Wallet balance: R{balance_data['balance']}")
            print(f"Currency: {balance_data.get('currency', 'ZAR')}")
            print(f"User ID: {balance_data['user_id']}")
            return balance_data
        else:
            raise ValueError(response.get('error', 'Failed to fetch wallet balance'))
    except requests.exceptions.RequestException as e:
        print(f"Error fetching wallet balance: {e}")
        return {}

# Usage
client = WayjaClient(API_KEY, SIGNING_SECRET)
user_token = 'wayja_token_from_oauth'
balance = client.get_wallet_balance(user_token)`,
      authenticateUser: `# Users must authenticate directly with Wayja
# For web applications, redirect users to Wayja login page

def redirect_to_wayja_login(redirect_uri: str) -> str:
    """Generate Wayja OAuth login URL."""
    from urllib.parse import quote
    encoded_redirect = quote(redirect_uri)
    return f"https://wayja.me/auth/login?redirect_uri={encoded_redirect}"

def handle_oauth_callback(request_url: str) -> Optional[Dict[str, str]]:
    """Extract user token from OAuth callback URL."""
    from urllib.parse import urlparse, parse_qs
    
    parsed = urlparse(request_url)
    params = parse_qs(parsed.query)
    
    user_token = params.get('wayja_token', [None])[0]
    user_id = params.get('wayja_user_id', [None])[0]
    
    if user_token and user_id:
        return {
            'wayja_token': user_token,
            'user_id': user_id
        }
    
    return None

# Usage in Flask/Django:
# @app.route('/wayja/callback')
# def wayja_callback():
#     auth_data = handle_oauth_callback(request.url)
#     if auth_data:
#         session['wayja_token'] = auth_data['wayja_token']
#         return redirect('/dashboard')
#     return redirect('/login')`,
      placeBet: `# Place a trade (requires HMAC signing)
import hmac
import hashlib
import base64
import uuid
import time

def generate_hmac_signature(self, method: str, path: str, timestamp: int, nonce: str, body: str) -> str:
    """Generate HMAC-SHA256 signature for request."""
    message = f"{method}\\n{path}\\n{timestamp}\\n{nonce}\\n{body}"
    signature = hmac.new(
        self.signing_secret.encode('utf-8'),
        message.encode('utf-8'),
        hashlib.sha256
    ).digest()
    return base64.b64encode(signature).decode('utf-8').replace('+', '-').replace('/', '_').replace('=', '')

def place_bet(self, option_id: int, amount: float, user_token: str) -> Dict[str, Any]:
    """Place a trade on a market option with HMAC signing."""
    if not self.signing_secret:
        raise ValueError("Signing secret required for place-bet endpoint")
    
    method = 'POST'
    # Note: Use /place-bet (not /functions/v1/place-bet) for HMAC signing
    path_for_signing = '/place-bet'
    full_path = '/functions/v1/place-bet'
    timestamp = int(time.time() * 1000)
    nonce = str(uuid.uuid4())
    body = json.dumps({
        "option_id": option_id,
        "amount": amount,
        "user_token": user_token
    })
    
    # Generate HMAC signature (use path_for_signing)
    signature = self.generate_hmac_signature(method, path_for_signing, timestamp, nonce, body)
    
    try:
        headers = {
            'X-API-Key': self.api_key,
            'Content-Type': 'application/json',
            'X-Request-Timestamp': str(timestamp),
            'X-Request-Nonce': nonce,
            'X-Request-Signature': signature
        }
        
        response = self.session.post(f"{self.base_url}{full_path}", data=body, headers=headers)
        response.raise_for_status()
        bet_data = response.json()['data']

        print(f"Trade placed successfully!")
        print(f"Trade ID: {bet_data['bet_id']}")
        print(f"Amount: R{bet_data['amount']}")
        print(f"Potential win: R{bet_data['potential_win']}")

        return bet_data
    except requests.exceptions.RequestException as e:
        print(f"Trade placement failed: {e}")
        return {}

# Complete betting workflow
def execute_betting_workflow(self, market_id: str, user_token: str, bet_amount: float = 50.0):
    """Complete workflow: check market, place trade."""
    # User token should be obtained from Wayja OAuth flow
    if not user_token:
        print("Error: User token required. User must authenticate with Wayja first.")
        return

    # Get market data
    market_data = self.get_market_data(market_id)
    if not market_data or not market_data['options']:
        print("No market options available")
        return

    # Find option with best odds
    best_option = max(market_data['options'], key=lambda x: x['odds'])

    print(f"Placing trade on: {best_option['name']} (odds: {best_option['odds']:.2f})")

    # Place trade
    bet_result = self.place_bet(best_option['id'], bet_amount, user_token)

    return bet_result

def create_market(self, market_data: Dict[str, Any]) -> Dict[str, Any]:
    """Create a new prediction market."""
    if not self.signing_secret:
        raise ValueError("Signing secret required for create-market endpoint")
    
    method = 'POST'
    # Note: Use /external-create-market (not /functions/v1/external-create-market) for HMAC signing
    path_for_signing = '/external-create-market'
    full_path = '/functions/v1/external-create-market'
    timestamp = int(time.time() * 1000)
    nonce = str(uuid.uuid4())
    body = json.dumps(market_data)
    
    # Generate HMAC signature (use path_for_signing)
    signature = self.generate_hmac_signature(method, path_for_signing, timestamp, nonce, body)
    
    try:
        headers = {
            'X-API-Key': self.api_key,
            'Content-Type': 'application/json',
            'X-Request-Timestamp': str(timestamp),
            'X-Request-Nonce': nonce,
            'X-Request-Signature': signature
        }
        
        response = self.session.post(f"{self.base_url}{full_path}", data=body, headers=headers)
        response.raise_for_status()
        market_result = response.json()
        
        print(f"Market created successfully!")
        print(f"Market ID: {market_result['market_id']}")
        print(f"Market URL: {market_result['market_url']}")
        
        return market_result
    except requests.exceptions.RequestException as e:
        print(f"Market creation failed: {e}")
        return {}

def top_up_wallet(self, amount: float, card_details: Dict[str, str], user_token: str) -> Dict[str, Any]:
    """Top up user wallet."""
    if not self.signing_secret:
        raise ValueError("Signing secret required for wallet-topup endpoint")
    
    method = 'POST'
    # Note: Use /wallet-topup (not /functions/v1/wallet-topup) for HMAC signing
    path_for_signing = '/wallet-topup'
    full_path = '/functions/v1/wallet-topup'
    timestamp = int(time.time() * 1000)
    nonce = str(uuid.uuid4())
    body = json.dumps({
        'user_token': user_token,
        'amount': amount,
        **card_details
    })
    
    # Generate HMAC signature (use path_for_signing)
    signature = self.generate_hmac_signature(method, path_for_signing, timestamp, nonce, body)
    
    try:
        headers = {
            'X-API-Key': self.api_key,
            'Content-Type': 'application/json',
            'X-Request-Timestamp': str(timestamp),
            'X-Request-Nonce': nonce,
            'X-Request-Signature': signature
        }
        
        response = self.session.post(f"{self.base_url}{full_path}", data=body, headers=headers)
        response.raise_for_status()
        topup_result = response.json()
        
        print(f"Top-up initiated!")
        if topup_result.get('data', {}).get('requires_redirect'):
            print(f"Redirect to payment: {topup_result['data']['redirect']}")
        
        return topup_result
    except requests.exceptions.RequestException as e:
        print(f"Top-up failed: {e}")
        return {}

# Usage
card_details = {
    'card_number': '4111111111111111',
    'card_holder': 'John Doe',
    'card_expiry_month': '12',
    'card_expiry_year': '2025',
    'card_cvv': '123'
}
client = WayjaClient(API_KEY, SIGNING_SECRET)
user_token = 'wayja_token_from_oauth'
topup_result = client.top_up_wallet(100.0, card_details, user_token)`,
      createMarket: `# Create a new market
def create_market(self, market_data: Dict[str, Any]) -> Dict[str, Any]:
    """Create a new prediction market."""
    if not self.signing_secret:
        raise ValueError("Signing secret required for create-market endpoint")
    
    method = 'POST'
    # Note: Use /external-create-market (not /functions/v1/external-create-market) for HMAC signing
    path_for_signing = '/external-create-market'
    full_path = '/functions/v1/external-create-market'
    timestamp = int(time.time() * 1000)
    nonce = str(uuid.uuid4())
    body = json.dumps(market_data)
    
    # Generate HMAC signature (use path_for_signing)
    signature = self.generate_hmac_signature(method, path_for_signing, timestamp, nonce, body)
    
    try:
        headers = {
            'X-API-Key': self.api_key,
            'Content-Type': 'application/json',
            'X-Request-Timestamp': str(timestamp),
            'X-Request-Nonce': nonce,
            'X-Request-Signature': signature
        }
        
        response = self.session.post(f"{self.base_url}{full_path}", data=body, headers=headers)
        response.raise_for_status()
        market_result = response.json()
        
        print(f"Market created successfully!")
        print(f"Market ID: {market_result['market_id']}")
        print(f"Market URL: {market_result['market_url']}")
        
        return market_result
    except requests.exceptions.RequestException as e:
        print(f"Market creation failed: {e}")
        return {}

# Usage
market_data = {
    'article_id': 'article-123',
    'title': 'Will Bitcoin reach $100k by end of 2024?',
    'description': 'Market description here...',
    'category': 'Crypto',
    'market_type': 'fixed',
    'options': [
        {'name': 'Yes'},
        {'name': 'No'}
    ],
    'resolution_time': '2024-12-31T23:59:59Z',
    'trading_end_time': '2024-12-31T20:00:00Z',  # Required for fixed markets
    'is_private': False
}
client = WayjaClient(API_KEY, SIGNING_SECRET)
result = client.create_market(market_data)`,
      topUpWallet: `# Top up wallet
def top_up_wallet(self, amount: float, card_details: Dict[str, str], user_token: str) -> Dict[str, Any]:
    """Top up user wallet."""
    if not self.signing_secret:
        raise ValueError("Signing secret required for wallet-topup endpoint")
    
    method = 'POST'
    # Note: Use /wallet-topup (not /functions/v1/wallet-topup) for HMAC signing
    path_for_signing = '/wallet-topup'
    full_path = '/functions/v1/wallet-topup'
    timestamp = int(time.time() * 1000)
    nonce = str(uuid.uuid4())
    body = json.dumps({
        'user_token': user_token,
        'amount': amount,
        **card_details
    })
    
    # Generate HMAC signature (use path_for_signing)
    signature = self.generate_hmac_signature(method, path_for_signing, timestamp, nonce, body)
    
    try:
        headers = {
            'X-API-Key': self.api_key,
            'Content-Type': 'application/json',
            'X-Request-Timestamp': str(timestamp),
            'X-Request-Nonce': nonce,
            'X-Request-Signature': signature
        }
        
        response = self.session.post(f"{self.base_url}{full_path}", data=body, headers=headers)
        response.raise_for_status()
        topup_result = response.json()
        
        print(f"Top-up initiated!")
        if topup_result.get('data', {}).get('requires_redirect'):
            print(f"Redirect to payment: {topup_result['data']['redirect']}")
        
        return topup_result
    except requests.exceptions.RequestException as e:
        print(f"Top-up failed: {e}")
        return {}

# Usage
card_details = {
    'card_number': '4111111111111111',
    'card_holder': 'John Doe',
    'card_expiry_month': '12',
    'card_expiry_year': '2025',
    'card_cvv': '123'
}
client = WayjaClient(API_KEY, SIGNING_SECRET)
user_token = 'wayja_token_from_oauth'
topup_result = client.top_up_wallet(100.0, card_details, user_token)`,
      completeExample: `# Complete trading bot example
import time
import random
from dataclasses import dataclass
from typing import List, Optional

@dataclass
class MarketOption:
    id: int
    name: str
    probability: float
    odds: float
    volume: float

@dataclass
class Market:
    id: str
    title: str
    status: str
    options: List[MarketOption]

class WayjaTradingBot(WayjaClient):
    def __init__(self, api_key: str, signing_secret: str, user_token: str = None):
        super().__init__(api_key, signing_secret)
        self.user_token: Optional[str] = user_token
        self.markets_cache: Dict[str, Market] = {}

    def set_user_token(self, token: str):
        """Set user token (obtained from Wayja OAuth flow)."""
        self.user_token = token
        print("User token set. Bot ready for trading!")

    def get_market(self, market_id: str) -> Optional[Market]:
        """Get market data with caching."""
        if market_id in self.markets_cache:
            return self.markets_cache[market_id]

        try:
            response = self._get(f"/market-data/{market_id}")
            market_data = response['data']

            options = [
                MarketOption(
                    id=opt['id'],
                    name=opt['name'],
                    probability=opt['probability'],
                    odds=opt['odds'],
                    volume=opt['volume']
                )
                for opt in market_data['options']
            ]

            market = Market(
                id=market_data['market_id'],
                title=market_data['title'],
                status=market_data['status'],
                options=options
            )

            self.markets_cache[market_id] = market
            return market
        except requests.exceptions.RequestException:
            return None

    def find_arbitrage_opportunity(self, market: Market) -> Optional[MarketOption]:
        """Simple strategy: trade on options with odds > 2.0."""
        eligible_options = [opt for opt in market.options if opt.odds > 2.0]
        return random.choice(eligible_options) if eligible_options else None

    def execute_trade(self, market_id: str, amount: float = 25.0):
        """Execute a single trade based on strategy."""
        market = self.get_market(market_id)
        if not market:
            print(f"Could not load market {market_id}")
            return False

        if market.status != 'active':
            print(f"Market {market_id} is not active")
            return False

        opportunity = self.find_arbitrage_opportunity(market)
        if not opportunity:
            print("No trading opportunities found")
            return False

        print(f"Found opportunity: {opportunity.name} at {opportunity.odds:.2f}x odds")

        if not self.user_token:
            print("Bot not authenticated")
            return False

        try:
            bet_result = self.place_bet(opportunity.id, amount, self.user_token)
            print(f"Trade executed successfully! Trade ID: {bet_result['bet_id']}")
            return True
        except Exception as e:
            print(f"Trade execution failed: {e}")
            return False

    def run_trading_loop(self, market_ids: List[str], interval_seconds: int = 300):
        """Run continuous trading loop."""
        print("Starting trading loop...")

        while True:
            for market_id in market_ids:
                self.execute_trade(market_id)
                time.sleep(1)  # Rate limiting

            print(f"Waiting {interval_seconds} seconds before next cycle...")
            time.sleep(interval_seconds)

# Usage
SIGNING_SECRET = 'YOUR_SIGNING_SECRET_HERE'
USER_TOKEN = 'wayja_token_from_oauth_callback'  # Obtained from Wayja OAuth flow

bot = WayjaTradingBot(API_KEY, SIGNING_SECRET, USER_TOKEN)

# Run on specific markets
markets_to_trade = ['market-123', 'market-456']
bot.run_trading_loop(markets_to_trade, interval_seconds=300)`
    },
    curl: {
      setup: `# Set your API credentials as environment variables
export WAYJA_API_KEY="YOUR_API_KEY_HERE"
export WAYJA_SIGNING_SECRET="YOUR_SIGNING_SECRET_HERE"  # Required for state-modifying endpoints
export WAYJA_BASE_URL="https://uyanvkwkikftzpanolmw.supabase.co/functions/v1"

# Helper function for authenticated GET requests (read-only, no HMAC needed)
wayja_get() {
    curl -s -H "X-API-Key: $WAYJA_API_KEY" \\
         -H "Content-Type: application/json" \\
         "$WAYJA_BASE_URL$1"
}

# Helper function for POST requests (requires HMAC signing for state-modifying endpoints)
wayja_post() {
    curl -s -X POST \\
         -H "X-API-Key: $WAYJA_API_KEY" \\
         -H "Content-Type: application/json" \\
         -d "$2" \\
         "$WAYJA_BASE_URL$1"
}

# Generate HMAC signature for requests
generate_hmac_signature() {
    local method=$1
    local path=$2
    local timestamp=$3
    local nonce=$4
    local body=$5
    
    local message="$method
$path
$timestamp
$nonce
$body"
    
    echo -n "$message" | openssl dgst -sha256 -hmac "$WAYJA_SIGNING_SECRET" -binary | base64 | tr '+/' '-_' | tr -d '='
}`,
      marketData: `# Get market data
# Usage: get_market_data <market_id>

get_market_data() {
    local market_id=$1
    echo "Fetching market data for: $market_id"

    wayja_get "/market-data/$market_id" | jq '.'
}

# Get markets by article ID
get_markets_by_article() {
    local article_id=$1
    echo "Fetching markets for article: $article_id"
    
    wayja_get "/market-data/article/$article_id" | jq '.'
}

# Example usage
get_market_data "market-123"
get_markets_by_article "bitcoin-analysis-2024"`,
      walletBalance: `# Get wallet balance (read-only, no HMAC signing required)
get_wallet_balance() {
    local user_token=$1
    
    if [ -z "$user_token" ]; then
        echo "Error: User token required"
        return 1
    fi
    
    # URL encode the token
    local encoded_token=$(printf '%s' "$user_token" | jq -sRr @uri)
    
    echo "Fetching wallet balance..."
    wayja_get "/wallet-balance?user_token=$encoded_token" | jq '.'
}

# Usage
get_wallet_balance "wayja_token_from_oauth"`,
      authenticateUser: `# Users must authenticate directly with Wayja
# For web applications, redirect users to Wayja login page

# Generate Wayja OAuth login URL
redirect_to_wayja_login() {
    local redirect_uri=$1
    local encoded_redirect=$(printf '%s' "$redirect_uri" | jq -sRr @uri)
    echo "https://wayja.me/auth/login?redirect_uri=$encoded_redirect"
}

# Extract user token from OAuth callback URL
handle_oauth_callback() {
    local callback_url=$1
    
    # Extract wayja_token and wayja_user_id from URL
    USER_TOKEN=$(echo "$callback_url" | grep -oP 'wayja_token=[^&]*' | cut -d= -f2 || echo "")
    USER_ID=$(echo "$callback_url" | grep -oP 'wayja_user_id=[^&]*' | cut -d= -f2 || echo "")
    
    if [ -n "$USER_TOKEN" ] && [ -n "$USER_ID" ]; then
        echo "User authenticated: $USER_ID"
        echo "Token stored: \${USER_TOKEN:0:20}..."
        return 0
    fi
    
    return 1
}

# Usage:
# 1. Redirect user: redirect_to_wayja_login "https://your-app.com/callback"
# 2. Handle callback: handle_oauth_callback "$CALLBACK_URL"
# 3. Use USER_TOKEN for subsequent API calls`,
      placeBet: `# Place a trade (requires HMAC signing)
# Usage: place_bet <option_id> <amount> <signing_secret> [user_token]

generate_hmac_signature() {
    local method=$1
    local path=$2
    local timestamp=$3
    local nonce=$4
    local body=$5
    local secret=$6
    
    local message="$method
$path
$timestamp
$nonce
$body"
    
    echo -n "$message" | openssl dgst -sha256 -hmac "$secret" -binary | base64 | tr '+/' '-_' | tr -d '='
}

place_bet() {
    local option_id=$1
    local amount=$2
    local signing_secret=$3
    local user_token=\${4:-$USER_TOKEN}

    if [ -z "$user_token" ]; then
        echo "Error: No user token available. Please authenticate first."
        return 1
    fi
    
    if [ -z "$signing_secret" ]; then
        echo "Error: Signing secret required for place-bet endpoint."
        return 1
    fi

    echo "Placing trade: Option $option_id, Amount R$amount"

    local method="POST"
    # Note: Use /place-bet (not /functions/v1/place-bet) for HMAC signing
    local path_for_signing="/place-bet"
    local full_path="/functions/v1/place-bet"
    local timestamp=$(date +%s000)
    local nonce=$(uuidgen)
    local body=$(cat <<EOF
{
  "option_id": $option_id,
  "amount": $amount,
  "user_token": "$user_token"
}
EOF
)
    
    # Generate HMAC signature (use path_for_signing)
    local signature=$(generate_hmac_signature "$method" "$path_for_signing" "$timestamp" "$nonce" "$body" "$signing_secret")
    
    curl -s -X POST \\
         -H "X-API-Key: $WAYJA_API_KEY" \\
         -H "Content-Type: application/json" \\
         -H "X-Request-Timestamp: $timestamp" \\
         -H "X-Request-Nonce: $nonce" \\
         -H "X-Request-Signature: $signature" \\
         -d "$body" \\
         "$WAYJA_BASE_URL$full_path"
}

# Cancel a trade
cancel_bet() {
    local bet_id=$1
    local user_token=\${2:-$USER_TOKEN}

    echo "Cancelling trade: $bet_id"

    local cancel_data=$(cat <<EOF
{
  "bet_id": "$bet_id"
}
EOF
)

    wayja_post "/cancel-bet" "$cancel_data"
}

# Create a new market
create_market() {
    local article_id=$1
    local title=$2
    local description=$3
    local category=$4
    local signing_secret=$5
    
    if [ -z "$signing_secret" ]; then
        echo "Error: Signing secret required for create-market endpoint."
        return 1
    fi
    
    echo "Creating market: $title"
    
    local method="POST"
    # Note: Use /external-create-market (not /functions/v1/external-create-market) for HMAC signing
    local path_for_signing="/external-create-market"
    local full_path="/functions/v1/external-create-market"
    local timestamp=$(date +%s000)
    local nonce=$(uuidgen)
    local body=$(cat <<EOF
{
  "article_id": "$article_id",
  "title": "$title",
  "description": "$description",
  "category": "$category",
  "market_type": "fixed",
  "options": [
    {"name": "Yes"},
    {"name": "No"}
  ],
  "resolution_time": "2024-12-31T23:59:59Z",
  "trading_end_time": "2024-12-31T20:00:00Z",
  "is_private": false
}
EOF
)
    
    local signature=$(generate_hmac_signature "$method" "$path_for_signing" "$timestamp" "$nonce" "$body" "$signing_secret")
    
    curl -s -X POST \\
         -H "X-API-Key: $WAYJA_API_KEY" \\
         -H "Content-Type: application/json" \\
         -H "X-Request-Timestamp: $timestamp" \\
         -H "X-Request-Nonce: $nonce" \\
         -H "X-Request-Signature: $signature" \\
         -d "$body" \\
         "$WAYJA_BASE_URL$full_path" | jq '.'
}

# Top up wallet
top_up_wallet() {
    local amount=$1
    local user_token=$2
    local signing_secret=$3
    
    if [ -z "$signing_secret" ]; then
        echo "Error: Signing secret required for wallet-topup endpoint."
        return 1
    fi
    
    echo "Topping up wallet: R$amount"
    
    local method="POST"
    # Note: Use /wallet-topup (not /functions/v1/wallet-topup) for HMAC signing
    local path_for_signing="/wallet-topup"
    local full_path="/functions/v1/wallet-topup"
    local timestamp=$(date +%s000)
    local nonce=$(uuidgen)
    local body=$(cat <<EOF
{
  "user_token": "$user_token",
  "amount": $amount,
  "card_number": "4111111111111111",
  "card_holder": "John Doe",
  "card_expiry_month": "12",
  "card_expiry_year": "2025",
  "card_cvv": "123"
}
EOF
)
    
    local signature=$(generate_hmac_signature "$method" "$path" "$timestamp" "$nonce" "$body" "$signing_secret")
    
    curl -s -X POST \\
         -H "X-API-Key: $WAYJA_API_KEY" \\
         -H "Content-Type: application/json" \\
         -H "X-Request-Timestamp: $timestamp" \\
         -H "X-Request-Nonce: $nonce" \\
         -H "X-Request-Signature: $signature" \\
         -d "$body" \\
         "$WAYJA_BASE_URL$path" | jq '.'`,
      completeExample: `# Complete trading script example
#!/bin/bash

# Simple automated trading bot for Wayja markets
# This script monitors markets and places bets on underdog options

# Configuration
API_KEY="YOUR_API_KEY_HERE"
SIGNING_SECRET="YOUR_SIGNING_SECRET_HERE"
BASE_URL="https://uyanvkwkikftzpanolmw.supabase.co/functions/v1"
MARKET_IDS=("market-123" "market-456")
BET_AMOUNT=25
USER_TOKEN=""  # Obtained from Wayja OAuth callback

# Helper functions
wayja_get() {
    curl -s -H "X-API-Key: $API_KEY" \\
         -H "Content-Type: application/json" \\
         "$BASE_URL$1"
}

wayja_post() {
    curl -s -X POST \\
         -H "X-API-Key: $API_KEY" \\
         -H "Content-Type: application/json" \\
         -d "$2" \\
         "$BASE_URL$1"
}

# Generate HMAC signature
generate_hmac_signature() {
    local method=$1
    local path=$2
    local timestamp=$3
    local nonce=$4
    local body=$5
    
    local message="$method
$path
$timestamp
$nonce
$body"
    
    echo -n "$message" | openssl dgst -sha256 -hmac "$SIGNING_SECRET" -binary | base64 | tr '+/' '-_' | tr -d '='
}

# Note: Users must authenticate via Wayja OAuth flow
# USER_TOKEN should be set from OAuth callback

# Get market data
get_market() {
    wayja_get "/market-data/$1"
}

# Place trade (with HMAC signing)
place_bet() {
    local option_id=$1
    local amount=$2
    
    if [ -z "$USER_TOKEN" ]; then
        echo "Error: USER_TOKEN not set. User must authenticate via Wayja OAuth."
        return 1
    fi
    
    local method="POST"
    # Note: Use /place-bet (not /functions/v1/place-bet) for HMAC signing
    local path_for_signing="/place-bet"
    local full_path="/functions/v1/place-bet"
    local timestamp=$(date +%s000)
    local nonce=$(uuidgen)
    local body=$(cat <<EOF
{
  "option_id": $option_id,
  "amount": $amount,
  "user_token": "$USER_TOKEN"
}
EOF
)
    
    local signature=$(generate_hmac_signature "$method" "$path_for_signing" "$timestamp" "$nonce" "$body")
    
    curl -s -X POST \\
         -H "X-API-Key: $API_KEY" \\
         -H "Content-Type: application/json" \\
         -H "X-Request-Timestamp: $timestamp" \\
         -H "X-Request-Nonce: $nonce" \\
         -H "X-Request-Signature: $signature" \\
         -d "$body" \\
         "$BASE_URL$full_path"
}`,
      marketDataByArticle: `# Get markets by article ID
get_markets_by_article() {
    local article_id=$1
    echo "Fetching markets for article: $article_id"
    
    wayja_get "/market-data/article/$article_id" | jq '.'
}

# Usage
get_markets_by_article "bitcoin-analysis-2024"`,
      createMarket: `# Create a new market
create_market() {
    local article_id=$1
    local title=$2
    local description=$3
    local category=$4
    local signing_secret=$5
    
    if [ -z "$signing_secret" ]; then
        echo "Error: Signing secret required for create-market endpoint."
        return 1
    fi
    
    echo "Creating market: $title"
    
    local method="POST"
    # Note: Use /external-create-market (not /functions/v1/external-create-market) for HMAC signing
    local path_for_signing="/external-create-market"
    local full_path="/functions/v1/external-create-market"
    local timestamp=$(date +%s000)
    local nonce=$(uuidgen)
    local body=$(cat <<EOF
{
  "article_id": "$article_id",
  "title": "$title",
  "description": "$description",
  "category": "$category",
  "market_type": "fixed",
  "options": [
    {"name": "Yes"},
    {"name": "No"}
  ],
  "resolution_time": "2024-12-31T23:59:59Z",
  "trading_end_time": "2024-12-31T20:00:00Z",
  "is_private": false
}
EOF
)
    
    local signature=$(generate_hmac_signature "$method" "$path" "$timestamp" "$nonce" "$body" "$signing_secret")
    
    curl -s -X POST \\
         -H "X-API-Key: $WAYJA_API_KEY" \\
         -H "Content-Type: application/json" \\
         -H "X-Request-Timestamp: $timestamp" \\
         -H "X-Request-Nonce: $nonce" \\
         -H "X-Request-Signature: $signature" \\
         -d "$body" \\
         "$WAYJA_BASE_URL$path" | jq '.'

# Usage
create_market "article-123" "Will Bitcoin reach $100k?" "Market description" "Crypto" "$WAYJA_SIGNING_SECRET"`,
      topUpWallet: `# Top up wallet
top_up_wallet() {
    local amount=$1
    local user_token=$2
    local signing_secret=$3
    
    if [ -z "$signing_secret" ]; then
        echo "Error: Signing secret required for wallet-topup endpoint."
        return 1
    fi
    
    echo "Topping up wallet: R$amount"
    
    local method="POST"
    # Note: Use /wallet-topup (not /functions/v1/wallet-topup) for HMAC signing
    local path_for_signing="/wallet-topup"
    local full_path="/functions/v1/wallet-topup"
    local timestamp=$(date +%s000)
    local nonce=$(uuidgen)
    local body=$(cat <<EOF
{
  "user_token": "$user_token",
  "amount": $amount,
  "card_number": "4111111111111111",
  "card_holder": "John Doe",
  "card_expiry_month": "12",
  "card_expiry_year": "2025",
  "card_cvv": "123"
}
EOF
)
    
    local signature=$(generate_hmac_signature "$method" "$path" "$timestamp" "$nonce" "$body" "$signing_secret")
    
    curl -s -X POST \\
         -H "X-API-Key: $WAYJA_API_KEY" \\
         -H "Content-Type: application/json" \\
         -H "X-Request-Timestamp: $timestamp" \\
         -H "X-Request-Nonce: $nonce" \\
         -H "X-Request-Signature: $signature" \\
         -d "$body" \\
         "$WAYJA_BASE_URL$path" | jq '.'

# Usage
top_up_wallet 100 "wayja_token" "$WAYJA_SIGNING_SECRET"`
    }
  };

  return (
    <div className="min-h-screen bg-wayja-cream">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-wayja-black">Code Examples</h1>
              <p className="text-wayja-dark-slate mt-2">Ready-to-use code samples in multiple programming languages</p>
            </div>
            <Link
              to="/developers"
              className="text-wayja-teal hover:text-wayja-dark-teal font-medium"
            >
              ← Back to Developer Portal
            </Link>
          </div>
        </div>
      </div>

      {/* Language Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-1">
            {languages.map((lang) => (
              <button
                key={lang.id}
                onClick={() => setActiveTab(lang.id)}
                className={`flex items-center space-x-2 px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === lang.id
                    ? 'border-wayja-teal text-wayja-teal'
                    : 'border-transparent text-wayja-slate hover:text-wayja-dark-slate hover:border-wayja-gray'
                }`}
              >
                {lang.icon}
                <span>{lang.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Examples List */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Examples</h2>
              <div className="space-y-2">
                {[
                  { id: 'setup', title: 'Setup & Configuration', icon: '⚙️' },
                  { id: 'marketData', title: 'Fetch Market Data', icon: '📊' },
                  { id: 'marketDataByArticle', title: 'Get Markets by Article', icon: '📰' },
                  { id: 'walletBalance', title: 'Get Wallet Balance', icon: '💵' },
                  { id: 'authenticateUser', title: 'User Authentication', icon: '🔐' },
                  { id: 'placeBet', title: 'Place Trades', icon: '💰' },
                  { id: 'createMarket', title: 'Create Market', icon: '➕' },
                  { id: 'topUpWallet', title: 'Top Up Wallet', icon: '💳', badge: 'Coming Soon' },
                  { id: 'completeExample', title: 'Complete Trading Bot', icon: '🤖' }
                ].map((example) => (
                  <div
                    key={example.id}
                    className="p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => document.getElementById(example.id)?.scrollIntoView({ behavior: 'smooth' })}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-lg">{example.icon}</span>
                        <span className="font-medium text-gray-900">{example.title}</span>
                      </div>
                      {'badge' in example && example.badge && (
                        <span className="px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs font-medium rounded-full">
                          {example.badge}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Code Examples */}
          <div className="lg:col-span-2 space-y-8">
            {/* Setup */}
            <div id="setup" className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">⚙️ Setup & Configuration</h3>
                <p className="text-gray-600 mt-2">Get started with Wayja API integration</p>
              </div>
              <div className="p-6">
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">{languages.find(l => l.id === activeTab)?.name}</span>
                    <button
                      onClick={() => copyToClipboard(examples[activeTab as keyof typeof examples].setup, 'setup')}
                      className="text-gray-400 hover:text-white"
                    >
                      {copiedCode === 'setup' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap">{examples[activeTab as keyof typeof examples].setup}</pre>
                </div>
              </div>
            </div>

            {/* Market Data */}
            <div id="marketData" className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">📊 Fetch Market Data</h3>
                <p className="text-gray-600 mt-2">Retrieve real-time market information and odds</p>
              </div>
              <div className="p-6">
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">{languages.find(l => l.id === activeTab)?.name}</span>
                    <button
                      onClick={() => copyToClipboard(examples[activeTab as keyof typeof examples].marketData, 'marketData')}
                      className="text-gray-400 hover:text-white"
                    >
                      {copiedCode === 'marketData' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap">{examples[activeTab as keyof typeof examples].marketData}</pre>
                </div>
              </div>
            </div>

            {/* Wallet Balance */}
            <div id="walletBalance" className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">💵 Get Wallet Balance</h3>
                <p className="text-gray-600 mt-2">Retrieve user wallet balance (read-only endpoint, no HMAC signing required)</p>
              </div>
              <div className="p-6">
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">{languages.find(l => l.id === activeTab)?.name}</span>
                    <button
                      onClick={() => copyToClipboard(examples[activeTab as keyof typeof examples].walletBalance, 'walletBalance')}
                      className="text-gray-400 hover:text-white"
                    >
                      {copiedCode === 'walletBalance' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap">{examples[activeTab as keyof typeof examples].walletBalance}</pre>
                </div>
              </div>
            </div>

            {/* Authentication */}
            <div id="authenticateUser" className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">🔐 User Authentication</h3>
                <p className="text-gray-600 mt-2">Authenticate users for betting operations</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-yellow-800 mb-1">Security Update</h4>
                      <p className="text-yellow-700 text-sm">
                        SSO authentication has been removed due to security vulnerabilities. Users must now authenticate directly with Wayja using OAuth 2.0 redirect flow. This ensures proper user consent, password verification, and compliance with financial regulations.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">{languages.find(l => l.id === activeTab)?.name}</span>
                    <button
                      onClick={() => copyToClipboard(examples[activeTab as keyof typeof examples].authenticateUser, 'authenticateUser')}
                      className="text-gray-400 hover:text-white"
                    >
                      {copiedCode === 'authenticateUser' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap">{examples[activeTab as keyof typeof examples].authenticateUser}</pre>
                </div>
              </div>
            </div>

            {/* Place Trade */}
            <div id="placeBet" className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">💰 Place Trades</h3>
                <p className="text-gray-600 mt-2">Execute betting operations programmatically</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-blue-800 mb-1">HMAC Signing Required</h4>
                      <p className="text-blue-700 text-sm">
                        The <code className="bg-blue-100 px-1 rounded">place-bet</code> endpoint requires HMAC request signing for security. 
                        You must include <code className="bg-blue-100 px-1 rounded">X-Request-Timestamp</code>, <code className="bg-blue-100 px-1 rounded">X-Request-Nonce</code>, 
                        and <code className="bg-blue-100 px-1 rounded">X-Request-Signature</code> headers signed with your signing secret.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">{languages.find(l => l.id === activeTab)?.name}</span>
                    <button
                      onClick={() => copyToClipboard(examples[activeTab as keyof typeof examples].placeBet, 'placeBet')}
                      className="text-gray-400 hover:text-white"
                    >
                      {copiedCode === 'placeBet' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap">{examples[activeTab as keyof typeof examples].placeBet}</pre>
                </div>
              </div>
            </div>

            {/* Create Market */}
            <div id="createMarket" className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">➕ Create Market</h3>
                <p className="text-gray-600 mt-2">Create a new prediction market (requires approved developer account)</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-blue-800 mb-1">HMAC Signing Required</h4>
                      <p className="text-blue-700 text-sm">
                        The <code className="bg-blue-100 px-1 rounded">create-market</code> endpoint requires HMAC request signing. 
                        You must include <code className="bg-blue-100 px-1 rounded">X-Request-Timestamp</code>, <code className="bg-blue-100 px-1 rounded">X-Request-Nonce</code>, 
                        and <code className="bg-blue-100 px-1 rounded">X-Request-Signature</code> headers signed with your signing secret.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">{languages.find(l => l.id === activeTab)?.name}</span>
                    <button
                      onClick={() => copyToClipboard(examples[activeTab as keyof typeof examples].createMarket, 'createMarket')}
                      className="text-gray-400 hover:text-white"
                    >
                      {copiedCode === 'createMarket' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap">{examples[activeTab as keyof typeof examples].createMarket}</pre>
                </div>
              </div>
            </div>

            {/* Top Up Wallet */}
            <div id="topUpWallet" className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <h3 className="text-xl font-semibold text-gray-900">💳 Top Up Wallet</h3>
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full">
                    Coming Soon
                  </span>
                </div>
                <p className="text-gray-600 mt-2">Add funds to a user wallet using Peach Payments</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-yellow-800 mb-1">🚧 Integration In Progress</h4>
                      <p className="text-yellow-700 text-sm mb-2">
                        The wallet top-up functionality is currently being integrated with Peach Payments:
                      </p>
                      <ul className="text-yellow-700 text-sm list-disc list-inside space-y-1">
                        <li><strong>Test Environment:</strong> Peach Sandbox integration</li>
                        <li><strong>Production Environment:</strong> Peach Production integration</li>
                      </ul>
                      <p className="text-yellow-700 text-sm mt-2">
                        This endpoint will be available soon. Check back later or contact support for updates.
                      </p>
                    </div>
                  </div>
                </div>
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-4">
                  <p className="text-gray-600 text-sm">
                    📝 <strong>Note:</strong> Code examples will be available once the Peach Payments integration is complete.
                  </p>
                </div>
              </div>
            </div>

            {/* Complete Example */}
            <div id="completeExample" className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900">🤖 Complete Trading Bot</h3>
                <p className="text-gray-600 mt-2">Full implementation of an automated trading system</p>
              </div>
              <div className="p-6">
                <div className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-400">{languages.find(l => l.id === activeTab)?.name}</span>
                    <button
                      onClick={() => copyToClipboard(examples[activeTab as keyof typeof examples].completeExample, 'completeExample')}
                      className="text-gray-400 hover:text-white"
                    >
                      {copiedCode === 'completeExample' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap">{examples[activeTab as keyof typeof examples].completeExample}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Run Example Button */}
      <div className="bg-wayja-light-teal/20 border-t border-wayja-teal/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h3 className="text-xl font-semibold text-wayja-black mb-4">Ready to Test Your Integration?</h3>
            <p className="text-wayja-dark-slate mb-6 max-w-2xl mx-auto">
              Try out the API endpoints with our interactive explorer or contact our developer support team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/developers/api-reference"
                className="bg-wayja-teal hover:bg-wayja-dark-teal text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                <Play className="w-4 h-4 inline mr-2" />
                API Reference
              </Link>
              <a
                href="mailto:support@wayja.me"
                className="border border-wayja-teal text-wayja-teal hover:bg-wayja-light-teal/20 px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Get Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CodeExamples;
